// El reloj del play sigue al video que se está viendo, no al revés —
// ADR-2026-11-03-el-play-sigue-al-video.
//
// Antes el reloj era de pared: sumaba el tiempo real en cada cuadro y, si un
// video quedaba más de 0,15 s atrás, lo forzaba con un seek. Con red lenta o
// videos pesados eso se encadenaba (el video espera datos, el reloj sigue, seek,
// vuelve a esperar…): el "tildado". Y como `play()` arranca tarde, el video
// quedaba siempre unos cuadros atrás del reloj; en cada corte esos cuadros no se
// veían nunca (el salto al dividir).
//
// Ahora, mientras haya un video activo en su tramo normal, el tiempo sale de ese
// video. Si está buscando o sin datos, el reloj lo espera. Si la espera se
// alarga (un archivo roto, un video que no arranca), sigue solo para no quedar
// trabado.

/** Lo que el reloj necesita saber del video que manda. */
export type VideoQueManda = {
  /** `clip.startTime` en la timeline. */
  inicio: number;
  /** `clip.duration` en la timeline. */
  duracion: number;
  /** `clip.mediaOffset`: dónde arranca el tramo dentro del archivo. */
  offset: number;
  /** Segundos de material por segundo de timeline. */
  velocidad: number;
  /** `el.currentTime`. */
  tiempoDelArchivo: number;
  /** Reproduciendo con datos: no está buscando, ni pausado, ni sin datos. */
  listo: boolean;
  /** Terminó el archivo o dio error: ya no puede mandar. */
  descartado: boolean;
  /** El navegador sigue bajando datos del archivo (`networkState` LOADING). */
  bajando?: boolean;
};

/** Cuánto se espera a un video que no está bajando nada antes de seguir sin él. */
export const ESPERA_MAXIMA_MS = 2500;
/** Mientras siguen llegando datos se lo espera más: con red lenta, seguir solo
 *  vuelve a encadenar seeks. */
export const ESPERA_MAXIMA_BAJANDO_MS = 20000;
/** Un video adelantado más que esto no se sigue: se lo corrige con un seek. */
export const ADELANTO_MAXIMO_S = 0.25;

/**
 * El tiempo de la timeline para este cuadro.
 *
 * `esperandoDesde` es el momento (performance.now) en que el reloj empezó a
 * esperar al video; `null` si no está esperando. Se devuelve actualizado.
 */
export function siguienteTiempo(p: {
  anterior: number;
  delta: number;
  ahora: number;
  manda: VideoQueManda | null;
  esperandoDesde: number | null;
}): { tiempo: number; esperandoDesde: number | null } {
  const libre = { tiempo: p.anterior + p.delta, esperandoDesde: null };
  const m = p.manda;
  if (!m || m.descartado || m.velocidad <= 0) return libre;

  const esperar = () => {
    const desde = p.esperandoDesde ?? p.ahora;
    const tope = m.bajando ? ESPERA_MAXIMA_BAJANDO_MS : ESPERA_MAXIMA_MS;
    if (p.ahora - desde > tope) {
      return { tiempo: p.anterior + p.delta, esperandoDesde: desde };
    }
    return { tiempo: p.anterior, esperandoDesde: desde };
  };

  if (!m.listo) return esperar();

  const fin = m.inicio + m.duracion;
  const segunElVideo = Math.min(
    fin,
    Math.max(m.inicio, m.inicio + (m.tiempoDelArchivo - m.offset) / m.velocidad),
  );
  // El video va atrás (recién arrancó, o un cuadro se demoró): se lo espera en
  // vez de dejarlo atrás. Así el cuadro del corte siempre se llega a ver.
  if (segunElVideo < p.anterior - 0.001) return esperar();
  // Muy adelantado: algo raro (un seek a medias). Que lo corrija el sync.
  if (segunElVideo > p.anterior + ADELANTO_MAXIMO_S) return libre;
  return { tiempo: segunElVideo, esperandoDesde: null };
}

/** Cuánto antes de su turno se deja listo el clip que viene. */
export const PREPARAR_ANTES_S = 1.5;

/**
 * Si el clip que viene tiene que dejarse ya parado en su primer cuadro, a qué
 * segundo del archivo. `null` si no hace falta (falta mucho, ya pasó, ya está
 * ahí o ya está buscando).
 *
 * Sin esto el `<video>` del clip siguiente llega al corte en el segundo 0 (o
 * donde lo dejó el último scrub): muestra un cuadro viejo, busca y arranca
 * tarde. Es el salto que se veía al dividir un clip.
 */
export function aDondePrepararlo(p: {
  inicio: number;
  offset: number;
  tiempo: number;
  tiempoDelArchivo: number;
  buscando: boolean;
}): number | null {
  const falta = p.inicio - p.tiempo;
  if (falta <= 0 || falta > PREPARAR_ANTES_S) return null;
  if (p.buscando) return null;
  if (Math.abs(p.tiempoDelArchivo - p.offset) <= 0.05) return null;
  return p.offset;
}
